/* eslint-disable max-lines */
import {
    Address,
    checkUniqueAddress,
    deleteCaptchaForAddress,
    TivanoData,
    getProducts,
    TivanoProductDetails,
    JarvisProduct,
    getJarvisProductsData,
    getProductDetails,
    JarvisRequestData,
    getSessionAvcResult,
    AvcSessionEntry,
    getAddressCookie,
    BIDResponse,
    setBID,
} from '@vfde-sails/avc-core';
import { select, put, call } from 'redux-saga/effects';
import { resetCaptcha, getCaptchaToken, listenForCaptchaVerify } from '../../../components/captcha';
import { isApiError } from '../util/typeGuard';
import { setAccessSupplier, setAddresses, setAvailabilityIndicator, setLoading } from '../action';
import { AddressForm } from '../interface';
import { selectAvcForm, selectAddresses } from '../selector';
import {
    openOverlay,
    setActiveOverlay,
    setHomesPassed,
    setLoading as setLpLoading,
    setTariffs,
} from '../../App/action';
import { OverlayContent } from '../../../enums/elementId';
import { Cookies, HttpStatus } from '../../../enums/network';
import { trackCaptcha } from '../../../util/tracking';
import {
    selectAddress,
    selectSelected,
    selectTariffs,
} from '../../App/selector';
import { updateTariffsWithAvcResult } from './jarvisHelper';
import { ErrorMessage } from '../enum';
import { filterOnlyMarketedPreFiberIds, filterOnlyRelevantIds } from './zpApiHelper';
import { getCMSConfig, getTariffIds } from '../../../util/cms';
import { SelectedTariff } from 'Container/App/interface';
import { HomesPassedType, MetacodePrefix, ProductType } from '../../../enums/product';
import { isObjectDeeplyChanged } from '../../../util/state';
import { deleteCookie } from '@vfde-sails/utils';

export function* addressSaga () {
    const { city, street, number, index }: AddressForm = yield select(selectAvcForm);
    const nonUniqueAddresses: Address[] = yield select(selectAddresses);

    const address: Address | undefined = nonUniqueAddresses[index];
    // if an adress was selected by the user find it based on the index and return it
    if (address) {
        yield put(setAddresses([address]));
        return address;
    }

    // otherwise check the address with the service
    const addressList: Address[] = yield call(checkUniqueAddress, city, street, number);

    yield put(setAddresses(addressList));

    switch (addressList.length){
        case 1:
            return addressList[0];
        case 0:
            throw new Error(ErrorMessage.ADDRESS_NOT_FOUND);
        default:
            throw new Error(ErrorMessage.NON_UNIQUE_ADDRESS);
    }
}

export function* captchaSaga () {
    yield call(resetCaptcha);

    // if captcha autovalidates after reset and we have a token then return
    if (getCaptchaToken()) {
        return;
    }

    // otherwise wait for user to verify captcha
    yield put(openOverlay(OverlayContent.CAPTCHA));
    yield put(setLoading(false));
    yield put(setLpLoading(false));
    yield call(trackCaptcha);
    yield call(listenForCaptchaVerify);
    yield put(setActiveOverlay(OverlayContent.FORM));
    yield put(setLoading());
}

export function* getLifeCycleData (address: Address, captcha: string) {
    yield call(deleteCookie, Cookies.OSHOP);

    const defaultBId: BIDResponse | null = yield call(setBID, '');
    const lifeCycleIds: string[] = yield call(getProducts, address, captcha, defaultBId?.vonr);
    const filteredIds = filterOnlyRelevantIds(lifeCycleIds);
    const lifeCycleDetails: TivanoProductDetails = filteredIds.length
        && (yield call(getProductDetails, address, filteredIds, defaultBId?.vonr));

    return {
        metacodes: lifeCycleIds,
        products: lifeCycleDetails ?? {},
    };
}
/**
 * getPreMarketingData
 * @param address
 * @param captcha
 */
export function* getPreMarketingData (address: Address, captcha: string) {
    yield call(deleteCookie, Cookies.OSHOP);

    const preFiberBId: BIDResponse | null = yield call(setBID, getCMSConfig().preFiberBannerId);
    const preFiberIds: string[] = yield call(getProducts, address, captcha, preFiberBId?.vonr);

    // Reset b_id to avoid stuck pre fiber checks
    yield call(deleteCookie, Cookies.OSHOP);

    const isBHS = preFiberIds.includes(getCMSConfig().homesPassedIds.markerBHS);
    const isBKV = preFiberIds.includes(getCMSConfig().homesPassedIds.markerBKV);

    if (isBHS && !isBKV) {
        yield put(setHomesPassed(HomesPassedType.BHS));

        return {
            metacodes: preFiberIds,
            products: {},
        };
    }

    if (isBKV) {
        yield put(setHomesPassed(HomesPassedType.BKV));
    }

    if (!isBHS && !isBKV) {
        yield put(setHomesPassed(undefined));
    }

    if (!preFiberIds.some(code => code.includes(MetacodePrefix.PRE_FIBER))) {
        return undefined;
    }

    const filteredIds = filterOnlyMarketedPreFiberIds(preFiberIds);
    const preFiberDetails: TivanoProductDetails = filteredIds.length
        && (yield call(getProductDetails, address, filteredIds, preFiberBId?.vonr));

    return {
        metacodes: filteredIds,
        products: preFiberDetails ?? {},
    };
}

export function* getTivanoData (address: Address, captcha: string) {
    const preMarketingData: TivanoData | undefined = yield call(getPreMarketingData, address, captcha);

    if (preMarketingData) {
        return preMarketingData;
    }

    const lifeCycleData: TivanoData = yield call(getLifeCycleData, address, captcha);

    return lifeCycleData;
}

export function* tivanoSaga (address: Address, isSeamless = false) {
    try {
        const previousAddress: Address | undefined = yield call(getAddressCookie);

        if (!isSeamless && isObjectDeeplyChanged(address, previousAddress)) {
            yield call(deleteCaptchaForAddress);
        }

        const result: TivanoData = yield call(getTivanoData, address, getCaptchaToken());

        return result;
    } catch (error) {
        if (!isApiError(error) || error.status !== HttpStatus.UNAUTHORIZED) {
            throw error;
        }

        yield call(captchaSaga);
    }

    const result: TivanoData = yield call(getTivanoData, address, getCaptchaToken());

    return result;
}

export function* updateOffersAfterVpSaga (tivano: TivanoProductDetails) {
    const tariffIds: string[] = yield call(getTariffIds);
    const selectedTariff: SelectedTariff = yield select(selectSelected);

    const requestData: JarvisRequestData = {
        salesChannel: 'PRE_FIB',
        selectedId: selectedTariff?.tariffId,
        tivano,
        productId: tariffIds,
    };

    const products: JarvisProduct[] = yield call(getJarvisProductsData, requestData);
    const currentTariffs: JarvisProduct[] = yield select(selectTariffs);
    const updatedTariffs: JarvisProduct[] = yield call(updateTariffsWithAvcResult, currentTariffs, products);

    yield put(setTariffs(updatedTariffs));
}

export function* seamlessAVCFromCache () {
    const addressFromState: Address = yield select(selectAddress);

    if (!addressFromState) {
        return null;
    }

    const avcResultFromSession: AvcSessionEntry = yield call(
        getSessionAvcResult,
        ProductType.PRE_FIBER,
        addressFromState,
        false,
    );

    return avcResultFromSession;
}

/**
    * Handles the response from Tivano
    * @param tivano - Tivano response
*/
export function* handleTivanoResponse(tivano: TivanoData){
    const products = Object.values(tivano.products);

    let accessSupplier: string | undefined;
    let availabilityIndicator: string | undefined;

    for (const product of products) {
        if (product.accessSupplier && product.availabilityIndicator) {
            accessSupplier = product.accessSupplier;
            availabilityIndicator = product.availabilityIndicator;
            break;
        }
    }

    yield put(setAccessSupplier(accessSupplier));
    yield put(setAvailabilityIndicator(availabilityIndicator));

    return {
        accessSupplier,
        availabilityIndicator,
    };
}
